import type { Chart, ChartType, Plugin } from 'chart.js';
import type { IncidentBand } from './opsCapacityUtils';

export interface IncidentBandsOptions {
  bands: IncidentBand[];
}

declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType> {
    incidentBands?: IncidentBandsOptions;
  }
}

/**
 * Shades the category-axis span of each manually curated incident and prints
 * its label at the top, so a demand spike can be read against the outage that
 * caused it. Pass via `plugins={[incidentBandsPlugin]}` and
 * `options.plugins.incidentBands.bands`.
 */
export const incidentBandsPlugin: Plugin<ChartType, IncidentBandsOptions> = {
  id: 'incidentBands',
  beforeDatasetsDraw(chart: Chart, _args, options) {
    const bands = options?.bands ?? [];
    if (bands.length === 0) return;
    const xScale = chart.scales.x;
    const { top, bottom } = chart.chartArea;
    if (!xScale) return;
    const { ctx } = chart;
    ctx.save();
    for (const band of bands) {
      const left = xScale.getPixelForValue(band.from) - halfStep(chart);
      const right = xScale.getPixelForValue(band.to) + halfStep(chart);
      ctx.fillStyle = 'rgba(239, 68, 68, 0.10)';
      ctx.fillRect(left, top, right - left, bottom - top);
      ctx.strokeStyle = 'rgba(239, 68, 68, 0.45)';
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(left, top);
      ctx.lineTo(left, bottom);
      ctx.moveTo(right, top);
      ctx.lineTo(right, bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgb(185, 28, 28)';
      ctx.font = '600 10px Instrument Sans, sans-serif';
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillText(`⚠ ${band.label}`, left + 4, top + 4);
    }
    ctx.restore();
  },
};

function halfStep(chart: Chart): number {
  const xScale = chart.scales.x;
  const n = xScale.ticks.length;
  if (n < 2) return (chart.chartArea.right - chart.chartArea.left) / 2;
  return Math.abs(xScale.getPixelForValue(1) - xScale.getPixelForValue(0)) / 2;
}
